import type { DialModel, SectionModel, SegmentModel } from "@keydials/dial-renderer";

// ---------------------------------------------------------------------------
// The builder's wheel reflows as it's built (KEY-560): a new section or action point grows from nothing, a removed one shrinks
// away where it was, and everything else slides to make room. The renderer already draws an arc of weight 0 as no width at all,
// so a frame of the animation is just a model whose weights sit part of the way between before and after. Pure, so it's
// tested without a screen; `BuilderWheel` drives it with animation frames.
// ---------------------------------------------------------------------------

const weightOf = (weight: number | undefined): number => (weight === undefined ? 1 : Math.max(weight, 0));

/** Slow at both ends, so an arc eases in rather than jumping. */
const ease = (t: number): number => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

/**
 * Both lists in one order: the new order, with anything that's gone kept where it used to be (just after the nearest earlier
 * neighbour that's still there), so it shrinks in place rather than jumping to the end.
 */
function mergeByKey<T extends { key: string }>(before: readonly T[], after: readonly T[]): Array<{ before?: T; after?: T }> {
  const merged: Array<{ key: string; before?: T; after?: T }> = after.map((item) => ({
    key: item.key,
    after: item,
    before: before.find((b) => b.key === item.key),
  }));
  before.forEach((item, index) => {
    if (after.some((a) => a.key === item.key)) return;
    let insertAt = 0;
    for (let i = index - 1; i >= 0; i -= 1) {
      const found = merged.findIndex((m) => m.key === before[i].key);
      if (found >= 0) {
        insertAt = found + 1;
        break;
      }
    }
    merged.splice(insertAt, 0, { key: item.key, before: item });
  });
  return merged;
}

/** One frame on the way from `from` to `to`, `progress` running 0 to 1. */
export function motionFrame(from: DialModel, to: DialModel, progress: number): DialModel {
  const t = ease(Math.min(1, Math.max(0, progress)));
  const lerp = (a: number, b: number) => a + (b - a) * t;

  const sections: SectionModel[] = mergeByKey(from.sections, to.sections).map(({ before, after }) => {
    const base = (after ?? before)!;
    const segments: SegmentModel[] = mergeByKey(before?.segments ?? [], after?.segments ?? []).map((pair) => ({
      ...(pair.after ?? pair.before)!,
      weight: lerp(pair.before ? weightOf(pair.before.weight) : 0, pair.after ? weightOf(pair.after.weight) : 0),
    }));
    return {
      ...base,
      segments,
      weight: lerp(before ? weightOf(before.weight) : 0, after ? weightOf(after.weight) : 0),
    };
  });

  return { ...to, sections };
}

/** Whether two models have the same sections and action points in the same places, so a change needs no motion at all. */
export function sameStructure(a: DialModel, b: DialModel): boolean {
  return (
    a.sections.length === b.sections.length &&
    a.sections.every((section, i) => {
      const other = b.sections[i];
      return (
        section.key === other.key &&
        weightOf(section.weight) === weightOf(other.weight) &&
        section.segments.length === other.segments.length &&
        section.segments.every((segment, j) => segment.key === other.segments[j].key && weightOf(segment.weight) === weightOf(other.segments[j].weight))
      );
    })
  );
}
